import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import Modal from 'react-modal';
import axiosInstance from '../../utils/axiosInstance';
import { API_URL } from '../../utils/runtimeConfig';
import { ErrorContext } from '../../context/ErrorContext';
import { AuthContext } from '../../context/AuthContext';
import './css/AddAllianceBuildingModal.css';

const apiUrl = API_URL;

const normalizeId = (value) => {
  if (!value) return '';
  if (typeof value === 'object') {
    return String(value._id || value.id || '');
  }
  return String(value);
};

const hexToRgbString = (hexValue) => {
  const hex = String(hexValue || '').replace('#', '');
  if (!hex || (hex.length !== 3 && hex.length !== 6)) {
    return '0, 0, 0';
  }

  const normalizedHex = hex.length === 3
    ? hex.split('').map((char) => `${char}${char}`).join('')
    : hex;

  const parsed = Number.parseInt(normalizedHex, 16);
  if (!Number.isFinite(parsed)) {
    return '0, 0, 0';
  }

  const r = (parsed >> 16) & 255;
  const g = (parsed >> 8) & 255;
  const b = parsed & 255;
  return `${r}, ${g}, ${b}`;
};

const AddAllianceBuildingModal = ({
  isOpen,
  onRequestClose,
  onSelectAlliance,
  selectedTileAddType,
  selectedNationId,
  allianceNationOptions = [],
  alliancePlacementByType = {}
}) => {
  const axios = axiosInstance;
  const { showError } = useContext(ErrorContext);
  const { user } = useContext(AuthContext);

  const [allianceNationId, setAllianceNationId] = useState('');
  const [allianceQuery, setAllianceQuery] = useState('');
  const [alliances, setAlliances] = useState([]);
  const [isLoadingAlliances, setIsLoadingAlliances] = useState(false);

  const [createName, setCreateName] = useState('');
  const [createAcronym, setCreateAcronym] = useState('');
  const [createTerritoryColor, setCreateTerritoryColor] = useState('#2b4f72');
  const [isCreatingAlliance, setIsCreatingAlliance] = useState(false);

  const [modalError, setModalError] = useState('');
  const [pendingRelocationAlliance, setPendingRelocationAlliance] = useState(null);

  const selectedNationIdNormalized = normalizeId(selectedNationId);
  const currentUserNationId = normalizeId(user?.nation);
  const normalizedAllianceNationOptions = useMemo(() => {
    const optionById = new Map();
    allianceNationOptions.forEach((nation) => {
      const nationId = normalizeId(nation?._id || nation?.id || nation);
      if (!nationId || optionById.has(nationId)) return;
      optionById.set(nationId, nation);
    });

    if (selectedNationIdNormalized && !optionById.has(selectedNationIdNormalized)) {
      optionById.set(selectedNationIdNormalized, {
        _id: selectedNationIdNormalized,
        number: selectedNationIdNormalized
      });
    }

    return Array.from(optionById.values()).sort((left, right) => {
      const leftNumber = Number(left?.number ?? 0);
      const rightNumber = Number(right?.number ?? 0);
      return leftNumber - rightNumber;
    });
  }, [allianceNationOptions, selectedNationIdNormalized]);
  const selectedAllianceNationIdNormalized = useMemo(() => {
    const explicitSelection = normalizeId(allianceNationId);
    if (explicitSelection) return explicitSelection;
    return selectedNationIdNormalized;
  }, [allianceNationId, selectedNationIdNormalized]);
  const selectedAllianceNation = useMemo(
    () =>
      normalizedAllianceNationOptions.find(
        (nation) => normalizeId(nation?._id || nation?.id || nation) === selectedAllianceNationIdNormalized
      ) || null,
    [normalizedAllianceNationOptions, selectedAllianceNationIdNormalized]
  );
  const canCreateAllianceInSelectedNation =
    user?.role !== 'nationAdmin' || selectedAllianceNationIdNormalized === currentUserNationId;
  const getDefaultAllianceNationId = useCallback(() => {
    if (!normalizedAllianceNationOptions.length) {
      return selectedNationIdNormalized;
    }
    const hasSelectedMapNation = normalizedAllianceNationOptions.some(
      (nation) => normalizeId(nation?._id || nation?.id || nation) === selectedNationIdNormalized
    );
    if (hasSelectedMapNation) {
      return selectedNationIdNormalized;
    }
    return normalizeId(normalizedAllianceNationOptions[0]?._id || normalizedAllianceNationOptions[0]);
  }, [normalizedAllianceNationOptions, selectedNationIdNormalized]);
  const placementByAllianceId = alliancePlacementByType[selectedTileAddType] || {};

  const filteredAlliances = useMemo(() => {
    const query = allianceQuery.trim().toLowerCase();
    if (!query) return alliances;
    return alliances.filter((alliance) => {
      const name = String(alliance.name || '').toLowerCase();
      const acronym = String(alliance.acronym || '').toLowerCase();
      return name.includes(query) || acronym.includes(query);
    });
  }, [allianceQuery, alliances]);

  const fetchAlliances = useCallback(async () => {
    if (!selectedAllianceNationIdNormalized) {
      setAlliances([]);
      return;
    }

    setIsLoadingAlliances(true);
    try {
      const response = await axios.get(`${apiUrl}/nations/${selectedAllianceNationIdNormalized}/alliances`);
      const sorted = (response.data || []).sort((a, b) => {
        const aLabel = `[${a.acronym || ''}] ${a.name || ''}`.toLowerCase();
        const bLabel = `[${b.acronym || ''}] ${b.name || ''}`.toLowerCase();
        return aLabel.localeCompare(bLabel);
      });
      setAlliances(sorted);
    } catch (error) {
      setAlliances([]);
      setModalError('Failed to load alliances.');
      showError(error?.response?.data?.message || 'Failed to load alliances.');
    } finally {
      setIsLoadingAlliances(false);
    }
  }, [axios, selectedAllianceNationIdNormalized, showError]);

  useEffect(() => {
    if (!isOpen) return;

    setAllianceNationId(getDefaultAllianceNationId());
    setAllianceQuery('');
    setModalError('');
    setPendingRelocationAlliance(null);
    setCreateName('');
    setCreateAcronym('');
    setCreateTerritoryColor('#2b4f72');
  }, [getDefaultAllianceNationId, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    fetchAlliances();
  }, [fetchAlliances, isOpen]);

  useEffect(() => {
    setPendingRelocationAlliance(null);
  }, [selectedAllianceNationIdNormalized]);

  const getStructurePlacement = (allianceId) => {
    const key = normalizeId(allianceId);
    return placementByAllianceId[key] || null;
  };

  const finalizeAllianceSelection = (alliance, options = {}) => {
    setPendingRelocationAlliance(null);
    onSelectAlliance(alliance, options);
    onRequestClose();
  };

  const handleAllianceSelect = (alliance) => {
    const structurePlacement = getStructurePlacement(alliance._id);

    if (structurePlacement) {
      setPendingRelocationAlliance({ alliance, structurePlacement });
      return;
    }

    finalizeAllianceSelection(alliance, { allowRelocateExisting: false });
  };

  const handleRelocateConfirm = () => {
    if (!pendingRelocationAlliance?.alliance) return;

    finalizeAllianceSelection(pendingRelocationAlliance.alliance, {
      allowRelocateExisting: true
    });
  };

  const handleCreateAlliance = async () => {
    const name = createName.trim();
    const acronym = createAcronym.trim().toUpperCase();

    if (!selectedAllianceNationIdNormalized) {
      setModalError('Select a nation before creating an alliance.');
      return;
    }
    if (!canCreateAllianceInSelectedNation) {
      setModalError('Your role cannot create alliances in this nation.');
      return;
    }

    if (!name) {
      setModalError('Alliance name is required.');
      return;
    }

    if (!/^[A-Z0-9]{3}$/.test(acronym)) {
      setModalError('Alliance acronym must be exactly 3 letters/numbers.');
      return;
    }

    setIsCreatingAlliance(true);
    setModalError('');

    try {
      const payload = {
        name,
        acronym,
        territoryColor: hexToRgbString(createTerritoryColor)
      };
      const response = await axios.post(`${apiUrl}/nations/${selectedAllianceNationIdNormalized}/alliances`, payload);
      finalizeAllianceSelection(response.data, { allowRelocateExisting: false });
    } catch (error) {
      setModalError(error?.response?.data?.message || 'Failed to create alliance.');
    } finally {
      setIsCreatingAlliance(false);
    }
  };

  if (!selectedTileAddType) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Select Alliance"
      className="add-alliance-building-modal"
      overlayClassName="add-alliance-building-modal-overlay"
    >
      <div className="add-alliance-building-modal-header">
        <h2>Select Alliance</h2>
        <p>
          Choose an existing alliance or create a new one for this
          {' '}
          {selectedTileAddType === 'allianceHall' ? 'Alliance Hall' : (selectedTileAddType || 'tile')}.
        </p>
      </div>

      {modalError && <div className="add-alliance-building-modal-error">{modalError}</div>}

      <div className="add-alliance-building-modal-grid">
        <section className="add-alliance-building-modal-column">
          <h3>Select Existing</h3>

          <label htmlFor="alliance-nation-filter">Alliance Nation</label>
          <select
            id="alliance-nation-filter"
            value={selectedAllianceNationIdNormalized}
            onChange={(event) => setAllianceNationId(event.target.value)}
          >
            {normalizedAllianceNationOptions.map((nationOption) => {
              const optionId = normalizeId(nationOption?._id || nationOption?.id || nationOption);
              const optionLabel = nationOption?.number ?? optionId;
              return (
                <option key={optionId} value={optionId}>
                  {optionLabel}
                </option>
              );
            })}
          </select>

          <label htmlFor="alliance-search">Alliance Search</label>
          <input
            id="alliance-search"
            type="text"
            value={allianceQuery}
            placeholder="Search alliance by name or acronym"
            onChange={(event) => setAllianceQuery(event.target.value)}
          />

          <div className="add-alliance-building-list" role="listbox" aria-label="Alliance options">
            {isLoadingAlliances && <p className="add-alliance-building-list-meta">Loading alliances...</p>}
            {!isLoadingAlliances && filteredAlliances.length === 0 && (
              <p className="add-alliance-building-list-meta">No alliances found for current search.</p>
            )}

            {!isLoadingAlliances && filteredAlliances.map((alliance) => {
              const structurePlacement = getStructurePlacement(alliance._id);
              return (
                <button
                  key={alliance._id}
                  type="button"
                  className={`add-alliance-building-item ${structurePlacement ? 'placed' : ''}`}
                  onClick={() => handleAllianceSelect(alliance)}
                >
                  <span className="add-alliance-building-left">
                    <span className="add-alliance-building-acronym">[{alliance.acronym || '---'}]</span>
                    <span className="add-alliance-building-name">{alliance.name}</span>
                  </span>
                  <span className={`add-alliance-building-coords ${structurePlacement ? 'has-placement' : ''}`}>
                    {structurePlacement ? `(${structurePlacement.x}, ${structurePlacement.y})` : ''}
                  </span>
                  <span className="add-alliance-building-state">
                    {structurePlacement ? 'Placed' : ''}
                  </span>
                </button>
              );
            })}
          </div>

          {pendingRelocationAlliance && (
            <div className="add-alliance-building-relocate-confirm">
              <p>
                <strong>[{pendingRelocationAlliance.alliance.acronym}] {pendingRelocationAlliance.alliance.name}</strong>
                {' '}already has this structure at
                {' '}
                ({pendingRelocationAlliance.structurePlacement.x}, {pendingRelocationAlliance.structurePlacement.y}).
              </p>
              <p>Move it to the selected destination?</p>
              <div className="add-alliance-building-relocate-actions">
                <button type="button" className="danger" onClick={handleRelocateConfirm}>Confirm Move</button>
                <button type="button" onClick={() => setPendingRelocationAlliance(null)}>Cancel</button>
              </div>
            </div>
          )}
        </section>

        <section className="add-alliance-building-modal-column">
          <h3>Create New</h3>
          <p className="add-alliance-building-list-meta">
            Nation: {(selectedAllianceNation?.number ?? selectedAllianceNationIdNormalized) || 'N/A'}
          </p>
          {!canCreateAllianceInSelectedNation && (
            <p className="add-alliance-building-list-meta">
              Nation Admins can only create alliances in their own nation.
            </p>
          )}

          <label htmlFor="create-alliance-name">Alliance Name *</label>
          <input
            id="create-alliance-name"
            type="text"
            value={createName}
            placeholder="Enter alliance name"
            onChange={(event) => setCreateName(event.target.value)}
            disabled={!canCreateAllianceInSelectedNation}
          />

          <label htmlFor="create-alliance-acronym">Acronym * (3 letters/numbers)</label>
          <input
            id="create-alliance-acronym"
            type="text"
            value={createAcronym}
            placeholder="ABC"
            onChange={(event) => setCreateAcronym(event.target.value.toUpperCase())}
            maxLength={3}
            disabled={!canCreateAllianceInSelectedNation}
          />

          <label htmlFor="create-alliance-color">Territory Color</label>
          <input
            id="create-alliance-color"
            type="color"
            value={createTerritoryColor}
            onChange={(event) => setCreateTerritoryColor(event.target.value)}
            className="add-alliance-building-color"
            disabled={!canCreateAllianceInSelectedNation}
          />

          <button
            type="button"
            className="add-alliance-building-create-button"
            onClick={handleCreateAlliance}
            disabled={isCreatingAlliance || !canCreateAllianceInSelectedNation}
          >
            {isCreatingAlliance ? 'Creating...' : 'Create And Select'}
          </button>
        </section>
      </div>

      <div className="add-alliance-building-modal-footer">
        <button type="button" onClick={onRequestClose}>Cancel</button>
      </div>
    </Modal>
  );
};

export default AddAllianceBuildingModal;
